import React, { useState, useRef, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";
import { Box, Backdrop, CircularProgress } from "@mui/material";
import axios from "axios";
import { baseURL } from "../../../core/repository/Repository";
import { IMAGE_URL, onToast } from "../../../core/Constant";

// Register image resize module
Quill.register("modules/imageResize", ImageResize);

// Extract image URLs from HTML
const extractImageUrls = (content) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, "text/html");
  const imageTags = doc.querySelectorAll("img");
  return Array.from(imageTags).map((img) => img.src);
};

const TextEditor = ({ onchange, value, label, placeholder, isError = false, error = "" }) => {
  const [isUploading, setIsUploading] = useState(false);
  const quillRef = useRef(null); // Ref to access Quill editor

  // Image upload handler using useCallback for toolbar stability
  const handleImageUpload = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const formData = new FormData();
      formData.append("image", file);

      try {
        setIsUploading(true);

        const res = await axios.post(
          `${baseURL}api/v1/admin/question/editor-image-upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (!res.data.status) {
          onToast(res.data.message, res.data, false);
        } else {
          const imageUrl = IMAGE_URL + res.data.data;

          const editor = quillRef.current?.getEditor?.();
          const range = editor?.getSelection(true);

          if (range) {
            // Important: Slight delay avoids internal Quill issue
            setTimeout(() => {
              editor.insertEmbed(range.index, "image", imageUrl);
              editor.setSelection(range.index + 1);
            }, 100);
          }
        }
      } catch (err) {
        console.error("Upload failed", err);
      } finally {
        setIsUploading(false);
      }
    };
  }, []);

  const modules = {
    toolbar: {
      container: [
        [{ header: "1" }, { header: "2" }, { font: [] }],
        [{ size: [] }],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        ["link", "image", "video"],
        ["clean"],
      ],
      handlers: {
        image: handleImageUpload,
      },
    },
    clipboard: {
      matchVisual: false,
    },
    imageResize: {
      modules: ["Resize", "DisplaySize"],
    },
  };

  const formats = [
    "header", "font", "size", "bold", "italic", "underline", "strike",
    "blockquote", "list", "bullet", "indent", "link", "image", "video",
  ];

  const handleChange = (content) => {
    const imageUrls = extractImageUrls(content);
    onchange(content, imageUrls);
  };

  return (
    <Box style={{ padding: 10 }}>
      <p style={{ marginBottom: 10, color: "#4E657C", fontSize: 19, fontWeight: 700 }}>
        {label}
      </p>
      <ReactQuill
        ref={quillRef}
        value={value}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        bounds="#root"
        placeholder={placeholder}
        className="text-editor"
      />
      {isError && <p className="input-error-text">{error}</p>}
      <Backdrop open={isUploading} sx={{ zIndex: 1301, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default TextEditor;
